// ════════════════════════════════════════════════════════════════════════════
//  ContaPagarEditDialog.tsx
//
//  Edição de uma conta a pagar EM ABERTO. Reusa o ContaPagarForm (o mesmo do
//  lançamento) pré-preenchido com o título carregado do servidor.
//
//  O backend (PUT contas-pagar/{id}) regera o carnê quando valor, quantidade de
//  parcelas ou vencimento mudam, e RECUSA (422) se já houver qualquer baixa —
//  por isso a tela só oferece a edição enquanto nada foi pago, e ainda confirma
//  aqui com os dados frescos.
// ════════════════════════════════════════════════════════════════════════════

import { useMemo } from "react";
import { AlertCircle } from "lucide-react";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { ContaPagarForm } from "./ContaPagarForm";
import { useDetalheContaPagar } from "../hooks/useDetalheContaPagar";
import type { ContaPagarFormData } from "../schemas/contaPagarSchema";
import type { AtualizarContaPagarRequest } from "../types";

interface ContaPagarEditDialogProps {
  tituloId: number | null;
  onOpenChange: (open: boolean) => void;
  onConfirm: (id: number, data: AtualizarContaPagarRequest) => void;
  isLoading?: boolean;
}

const soData = (iso: string) => iso.slice(0, 10);

/**
 * Reconstrói o campo "dias entre parcelas" a partir dos vencimentos das parcelas
 * já gravadas: cada número é o deslocamento ABSOLUTO da parcela em relação à 1ª
 * — exatamente o formato que o cronograma do backend consome. Assim, reeditar
 * sem mexer no carnê preserva as datas originais, em vez de cair no padrão 30/30.
 */
function reconstruirDias(vencimentos: string[]): string {
  if (vencimentos.length <= 1) return "";
  const emDias = (iso: string) => {
    const [a, m, d] = soData(iso).split("-").map(Number);
    return new Date(a, m - 1, d).getTime();
  };
  const primeiro = emDias(vencimentos[0]);
  return vencimentos
    .slice(1)
    .map((v) => Math.round((emDias(v) - primeiro) / 86_400_000))
    .join(",");
}

export function ContaPagarEditDialog({
  tituloId, onOpenChange, onConfirm, isLoading,
}: ContaPagarEditDialogProps) {
  const { data: conta, isLoading: carregando } = useDetalheContaPagar(tituloId);

  // Espelha a guarda do servidor: qualquer baixa impede a edição do parcelamento.
  const temPagamento = !!conta && (
    conta.isPago || (conta.valorPago ?? 0) > 0 ||
    conta.baixas.some((b) => b.isPago || b.valorPago > 0)
  );

  const defaultValues = useMemo<Partial<ContaPagarFormData> | undefined>(() => {
    if (!conta) return undefined;
    const parcelas = [...conta.baixas].sort((a, b) => a.nroParcela - b.nroParcela);
    const vencs = parcelas.map((b) => b.dtVencimento);
    const qtd = conta.qtdParcelas > 0 ? conta.qtdParcelas : (vencs.length || 1);
    return {
      fornecedorId:    conta.fornecedorId,
      grupoDespesasId: conta.grupoDespesasId,
      tipoDespesasId:  conta.tipoDespesasId,
      centroCustoId:   conta.centroCustoId,
      contaContabilId: conta.contaContabilId,
      dtEmissao:       soData(conta.dtEmissao),
      // 1º vencimento é o da 1ª parcela; o título pode ter sido aberto por uma
      // linha que não é a primeira.
      dtVencimento:    soData(vencs[0] ?? conta.dtVencimento),
      // Valor é POR PARCELA: título = valorParcela × qtd, sem sobra de centavos.
      valorParcela:    Number((conta.valorTotal / qtd).toFixed(2)),
      qtdParcelas:     qtd,
      diasParcelas:    reconstruirDias(vencs.length ? vencs : [conta.dtVencimento]),
      diaFixo:         false,
      numDocumento:    conta.numDocumento ?? undefined,
      lancamento:      conta.lancamento ?? undefined,
    };
  }, [conta]);

  function submeter(data: ContaPagarFormData) {
    if (!conta) return;

    // Classificação + metadados: vão SEMPRE (o backend aceita mesmo com baixa).
    // Classificação só quando tem valor (>0): título migrado vem com
    // grupo/tipo/centro = 0; enviar undefined faz o backend NÃO tocar nesses
    // campos em vez de gravar 0 por cima.
    const base: AtualizarContaPagarRequest = {
      fornecedorId:    data.fornecedorId,
      centroCustoId:   data.centroCustoId   || undefined,
      contaContabilId: data.contaContabilId || undefined,
      tipoDespesasId:  data.tipoDespesasId  || undefined,
      grupoDespesasId: data.grupoDespesasId || undefined,
      dtEmissao:       data.dtEmissao || undefined,
      numDocumento:    data.numDocumento,
      lancamento:      data.lancamento,
    };

    // Parcelamento só quando NÃO há pagamento — senão o backend recusa (422).
    const payload: AtualizarContaPagarRequest = temPagamento
      ? base
      : {
          ...base,
          dtVencimento: data.dtVencimento,
          valorParcela: data.valorParcela,
          qtdParcelas:  data.qtdParcelas,
          diasParcelas: data.diasParcelas || undefined,
          diaFixo:      data.diaFixo,
        };

    onConfirm(conta.id, payload);
  }

  return (
    <Dialog open={tituloId !== null} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Editar Conta a Pagar</DialogTitle>
          <DialogDescription>
            {temPagamento
              ? "Este título já tem pagamento. Corrija a classificação e os dados do documento; o parcelamento fica bloqueado."
              : "Altere os dados do título. Mudar valor, quantidade de parcelas ou vencimento regera o carnê."}
          </DialogDescription>
        </DialogHeader>

        {carregando || !conta ? (
          <div className="py-8 text-center text-muted-foreground">Carregando...</div>
        ) : (
          <ContaPagarForm
            // Remonta o formulário ao trocar de título — useForm só lê
            // defaultValues no mount.
            key={conta.id}
            defaultValues={defaultValues}
            bloquearParcelamento={temPagamento}
            onSubmit={submeter}
            onCancel={() => onOpenChange(false)}
            isLoading={isLoading}
            submitLabel="Salvar alterações"
            // Título migrado costuma vir sem classificação (grupo/tipo/centro=0);
            // não obrigar a classificar só para editar.
            exigirClassificacao={false}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}
